import { motion } from "framer-motion";
import type { Rect } from "./TourOverlay";

const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 12;

// Two render paths:
//   - rect=null  → flat dim layer (welcome / cta / mobile fallback). Looks
//                  like a modal backdrop.
//   - rect set   → full-screen SVG dim with the target rect punched out via
//                  a mask. Framer-motion animates the punch as steps change.
//
// `pointer-events: none` on both: we deliberately let clicks pass through
// to the underlying app. The provider snaps the URL back to the active
// step's route if the user wanders off-route — so an accidental sidebar
// click can't desynchronise the tour.
export function TourSpotlight({ rect }: { rect: Rect | null }) {
  if (!rect) {
    return (
      <motion.div
        aria-hidden="true"
        className="fixed inset-0 z-[1000] pointer-events-none"
        style={{ backgroundColor: "rgba(15,15,15,0.55)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
      />
    );
  }

  const x = rect.left - SPOTLIGHT_PADDING;
  const y = rect.top - SPOTLIGHT_PADDING;
  const w = rect.width + 2 * SPOTLIGHT_PADDING;
  const h = rect.height + 2 * SPOTLIGHT_PADDING;

  return (
    <svg
      aria-hidden="true"
      className="fixed inset-0 z-[1000] pointer-events-none"
      width="100%"
      height="100%"
      style={{ width: "100vw", height: "100vh" }}
    >
      <defs>
        <mask id="tour-spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <motion.rect
            initial={false}
            animate={{ x, y, width: w, height: h }}
            rx={SPOTLIGHT_RADIUS}
            ry={SPOTLIGHT_RADIUS}
            fill="black"
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(15,15,15,0.55)"
        mask="url(#tour-spotlight-mask)"
      />
    </svg>
  );
}
